//calculations.ts
import { AccountEntry, CategoryOption, EBITDARole } from '../types';

// Date-related utilities
export const monthMap: { [key: string]: number } = {
  'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
  'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
};

export const getQuarter = (month: string): string => {
  const [monthStr, yearStr] = month.split('-');
  const monthNum = monthMap[monthStr];
  const quarterNum = Math.floor(monthNum / 3) + 1;
  return `Q${quarterNum}'${yearStr}`;
};

export const getYear = (month: string): string => {
  const [, shortYear] = month.split('-');
  return (2000 + parseInt(shortYear)).toString();
};

export const getYearMonthRange = (year: string, months: string[]): string | null => {
  const yearMonths = months.filter(month => getYear(month) === year);
  const firstMonth = yearMonths[0].split('-')[0];
  const lastMonth = yearMonths[yearMonths.length - 1].split('-')[0];
  return firstMonth === 'Jan' && lastMonth === 'Dec' ? null : `${firstMonth}-${lastMonth}`;
};

// Helper function to convert month-year to comparable number
export const getMonthYearValue = (monthYear: string): number => {
  const [month, year] = monthYear.split('-');
  const monthNum = monthMap[month];
  const yearNum = parseInt(year);
  return (yearNum * 12) + monthNum;
};

// Sorting function for month-year strings
export const compareMonthYear = (a: string, b: string): number => {
  return getMonthYearValue(a) - getMonthYearValue(b);
};

// Get categories with a specific EBITDA role
export const getCategoriesByEBITDARole = (
  accounts: AccountEntry[],
  templateCategories: CategoryOption[],
  role: EBITDARole
): string[] => {
  if (!templateCategories) return [];
  
  return templateCategories
    .filter(category => category.ebitdaRole === role)
    .map(category => category.value);
};

// Financial calculations
export const calculateAccountsByPeriod = (
  accounts: AccountEntry[],
  category: string,
  period: string,
  viewType: 'monthly' | 'quarterly' | 'yearly'
): AccountEntry[] => {
  if (viewType === 'monthly') {
    return accounts.filter(account => 
      account.category === category && account.month === period
    );
  } else if (viewType === 'quarterly') {
    return accounts.filter(account => 
      account.category === category && getQuarter(account.month) === period
    );
  } else {
    return accounts.filter(account => 
      account.category === category && getYear(account.month) === period
    );
  }
};

export const calculateTTM = (
  accounts: AccountEntry[],
  category: string,
  allMonths: string[],
  isRevenue: boolean,
  isContraRevenue: boolean
): number => {
  // First, sort months using the compareMonthYear function
  const sortedMonths = [...allMonths].sort(compareMonthYear);
  const last12Months = sortedMonths.slice(Math.max(0, sortedMonths.length - 12));
  
  console.log('TTM Calculation Debug:');
  console.log('Category:', category);
  console.log('Sorted Months:', sortedMonths);
  console.log('Last 12 Months:', last12Months);
  
  return last12Months.reduce((total, month) => {
    const periodAccounts = accounts.filter(account => 
      account.category === category && account.month === month
    );
    
    const monthTotal = periodAccounts.reduce((acc, account) => {
      const amount = isRevenue || isContraRevenue
        ? account.credit - account.debit
        : account.debit - account.credit;
      
      console.log(`Month: ${month}, Amount: ${amount}`);
      return acc + amount;
    }, 0);
    
    return total + monthTotal;
  }, 0);
};

/**
 * Calculate TTM for a specific period type
 * @param accounts - Array of account entries
 * @param category - Category name
 * @param allMonths - All available months
 * @param isRevenue - Whether this is a revenue category
 * @param isContraRevenue - Whether this is a contra-revenue category
 * @param periodType - Period type: 'monthly', 'quarterly', or 'annual'
 * @returns TTM value adjusted for period type
 */
export const calculateTTMForPeriodType = (
  accounts: AccountEntry[],
  category: string,
  allMonths: string[],
  isRevenue: boolean,
  isContraRevenue: boolean,
  periodType: 'monthly' | 'quarterly' | 'annual'
): number => {
  // First, sort months using the compareMonthYear function
  const sortedMonths = [...allMonths].sort(compareMonthYear);
  
  let lookbackPeriods: string[];
  
  switch (periodType) {
    case 'quarterly':
      // Look back 4 quarters for TTM
      lookbackPeriods = sortedMonths.slice(-4);
      break;
    case 'annual':
      // For annual data, TTM should be the most recent year (closest approximation to trailing twelve months)
      lookbackPeriods = sortedMonths.slice(-1);
      break;
    default: // monthly
      // Traditional 12-month lookback
      lookbackPeriods = sortedMonths.slice(Math.max(0, sortedMonths.length - 12));
      break;
  }
  
  console.log(`TTM Calculation Debug (${periodType}):`);
  console.log('Category:', category);
  console.log('Sorted Months:', sortedMonths);
  console.log('Lookback Periods:', lookbackPeriods);
  
  return lookbackPeriods.reduce((total, month) => {
    const periodAccounts = accounts.filter(account => 
      account.category === category && account.month === month
    );
    
    const monthTotal = periodAccounts.reduce((acc, account) => {
      const amount = isRevenue || isContraRevenue
        ? account.credit - account.debit
        : account.debit - account.credit;
      
      console.log(`Period: ${month}, Amount: ${amount}`);
      return acc + amount;
    }, 0);
    
    return total + monthTotal;
  }, 0);
};

export const calculateCategoryTotal = (
  accounts: AccountEntry[],
  category: string,
  period: string,
  viewType: 'monthly' | 'quarterly' | 'yearly',
  isRevenue: boolean,
  isContraRevenue: boolean
): number => {
  const periodAccounts = calculateAccountsByPeriod(accounts, category, period, viewType);
  
  return periodAccounts.reduce((total, account) => {
    if (isRevenue) {
      // For revenue accounts, the credit balance should be positive
      return total + (account.credit - account.debit);
    } else if (isContraRevenue) {
      // For contra-revenue accounts, we want them to reduce revenue, 
      // so return them as negative values
      return total + (account.credit - account.debit);
    } else {
      // For expense accounts, keep normal debit balance (positive on P&L)
      return total + (account.debit - account.credit);
    }
  }, 0);
};

/**
 * Calculate annualized value for a specific period type
 * @param accounts - Array of account entries
 * @param category - Category name
 * @param period - Current period
 * @param isRevenue - Whether this is a revenue category
 * @param isContraRevenue - Whether this is a contra-revenue category
 * @param periodType - Period type: 'monthly', 'quarterly', or 'annual'
 * @param viewType - Current view type: 'monthly', 'quarterly', or 'yearly'
 * @returns Annualized value adjusted for period type
 */
export const calculateAnnualizedForPeriodType = (
  accounts: AccountEntry[],
  category: string,
  period: string,
  isRevenue: boolean,
  isContraRevenue: boolean,
  periodType: 'monthly' | 'quarterly' | 'annual',
  viewType: 'monthly' | 'quarterly' | 'yearly'
): number => {
  // Get the current period value
  const currentValue = calculateCategoryTotal(accounts, category, period, viewType, isRevenue, isContraRevenue);
  
  // Calculate annualization factor based on period type and view type
  let annualizationFactor: number;
  
  if (periodType === 'annual') {
    // Annual data is already annualized
    annualizationFactor = 1;
  } else if (periodType === 'quarterly') {
    // Quarterly data: 4 quarters = 1 year
    annualizationFactor = viewType === 'quarterly' ? 4 : 1;
  } else { // monthly
    // Monthly data: 12 months = 1 year
    annualizationFactor = viewType === 'monthly' ? 12 : 1;
  }
  
  return currentValue * annualizationFactor;
};

export const calculateSectionTotal = (
  accounts: AccountEntry[],
  categories: string[],
  period: string,
  viewType: 'monthly' | 'quarterly' | 'yearly',
  isRevenue: boolean,
  isContraRevenue: boolean
): number => {
  return categories.reduce((total, category) => {
    return total + calculateCategoryTotal(accounts, category, period, viewType, isRevenue, isContraRevenue);
  }, 0);
};

export const calculateEBITDAComponents = (
  accounts: AccountEntry[],
  period: string,
  viewType: 'monthly' | 'quarterly' | 'yearly'
) => {
  const interestExpense = calculateCategoryTotal(
    accounts,
    'Interest Expense (Income)',
    period,
    viewType,
    false,
    false
  );

  const incomeTaxExpense = calculateCategoryTotal(
    accounts,
    'Income Tax Expense',
    period,
    viewType,
    false,
    false
  );

  const depreciation = calculateCategoryTotal(
    accounts,
    'Depreciation',
    period,
    viewType,
    false,
    false
  );

  const amortization = calculateCategoryTotal(
    accounts,
    'Amortization',
    period,
    viewType,
    false,
    false
  );

  return {
    interestExpense,
    incomeTaxExpense,
    depreciation,
    amortization
  };
};

export const calculateEBITDA = (
  accounts: AccountEntry[],
  period: string,
  viewType: 'monthly' | 'quarterly' | 'yearly',
  netIncome: number
) => {
  const { 
    interestExpense,
    incomeTaxExpense,
    depreciation,
    amortization 
  } = calculateEBITDAComponents(accounts, period, viewType);

  return netIncome + interestExpense + incomeTaxExpense + depreciation + amortization;
};

export const calculateTTMEBITDA = (
  accounts: AccountEntry[],
  allMonths: string[],
  ttmNetIncome: number
) => {
  console.log('Debugging calculateTTMEBITDA');
  console.log('All Months (Before Sorting):', allMonths);

  // Sort months and take the last 12
  const sortedMonths = [...allMonths].sort(compareMonthYear); 
  const last12Months = sortedMonths.slice(-12);

  console.log('Last 12 Months:', last12Months);
  console.log('TTM Net Income:', ttmNetIncome);
  
  const ttmComponents = {
    interestExpense: calculateTTM(accounts, 'Interest Expense (Income)', last12Months, false, false),
    incomeTaxExpense: calculateTTM(accounts, 'Income Tax Expense', last12Months, false, false),
    depreciation: calculateTTM(accounts, 'Depreciation', last12Months, false, false),
    amortization: calculateTTM(accounts, 'Amortization', last12Months, false, false)
  };
  console.log('TTM Interest Expense:', ttmComponents.interestExpense);
  return ttmNetIncome + 
         ttmComponents.interestExpense + 
         ttmComponents.incomeTaxExpense + 
         ttmComponents.depreciation + 
         ttmComponents.amortization;
};

// Calculate EBITDA using the role-based approach
export const calculateRoleBasedEBITDA = (
  accounts: AccountEntry[],
  templateCategories: CategoryOption[],
  period: string,
  viewType: 'monthly' | 'quarterly' | 'yearly',
  netIncome: number
): number => {
  if (!templateCategories) return netIncome;
  
  let ebitda = netIncome;
  
  // Add back interest expense
  const interestCategories = getCategoriesByEBITDARole(accounts, templateCategories, 'interest-expense');
  ebitda += interestCategories.reduce((total, category) => 
    total + calculateCategoryTotal(accounts, category, period, viewType, false, false), 0);
  
  // Add back income tax expense
  const taxCategories = getCategoriesByEBITDARole(accounts, templateCategories, 'income-tax-expense');
  ebitda += taxCategories.reduce((total, category) => 
    total + calculateCategoryTotal(accounts, category, period, viewType, false, false), 0);
  
  // Add back depreciation
  const depreciationCategories = getCategoriesByEBITDARole(accounts, templateCategories, 'depreciation');
  ebitda += depreciationCategories.reduce((total, category) => 
    total + calculateCategoryTotal(accounts, category, period, viewType, false, false), 0);
  
  // Add back amortization
  const amortizationCategories = getCategoriesByEBITDARole(accounts, templateCategories, 'amortization');
  ebitda += amortizationCategories.reduce((total, category) => 
    total + calculateCategoryTotal(accounts, category, period, viewType, false, false), 0);
  
  return ebitda;
};

// Calculate TTM EBITDA using role-based approach
export const calculateRoleBasedTTMEBITDA = (
  accounts: AccountEntry[],
  templateCategories: CategoryOption[],
  allMonths: string[],
  ttmNetIncome: number
): number => {
  if (!templateCategories) return ttmNetIncome;
  
  // Sort months and take the last 12
  const sortedMonths = [...allMonths].sort(compareMonthYear); 
  const last12Months = sortedMonths.slice(-12);
  
  let ttmEBITDA = ttmNetIncome;
  
  // Add back TTM interest expense
  const interestCategories = getCategoriesByEBITDARole(accounts, templateCategories, 'interest-expense');
  interestCategories.forEach(category => {
    ttmEBITDA += calculateTTM(accounts, category, last12Months, false, false);
  });
  
  // Add back TTM income tax expense
  const taxCategories = getCategoriesByEBITDARole(accounts, templateCategories, 'income-tax-expense');
  taxCategories.forEach(category => {
    ttmEBITDA += calculateTTM(accounts, category, last12Months, false, false);
  });
  
  // Add back TTM depreciation
  const depreciationCategories = getCategoriesByEBITDARole(accounts, templateCategories, 'depreciation');
  depreciationCategories.forEach(category => {
    ttmEBITDA += calculateTTM(accounts, category, last12Months, false, false);
  });
  
  // Add back TTM amortization
  const amortizationCategories = getCategoriesByEBITDARole(accounts, templateCategories, 'amortization');
  amortizationCategories.forEach(category => {
    ttmEBITDA += calculateTTM(accounts, category, last12Months, false, false);
  });
  
  return ttmEBITDA;
};

// Helper function to calculate growth percentages
export const calculateGrowthPercentage = (
  currentValue: number, 
  previousValue: number
): string => {
  if (previousValue === 0) {
    // Avoid division by zero
    return previousValue === currentValue ? "0.0%" : "n/a";
  }
  
  const growthRate = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
  const sign = growthRate > 0 ? '+' : '';
  return `${sign}${growthRate.toFixed(1)}%`;
};

/**
 * Universal function to calculate days in a period based on period type
 * @param periods - Array of period strings (months for monthly, quarters for quarterly, years for annual)
 * @param periodType - Period type: 'monthly', 'quarterly', or 'annual'
 * @returns Total number of days in the period(s)
 */
export const calculatePeriodDays = (
  periods: string | string[],
  periodType: 'monthly' | 'quarterly' | 'annual'
): number => {
  const periodArray = Array.isArray(periods) ? periods : [periods];
  
  switch (periodType) {
    case 'monthly':
      // For monthly, calculate days in each month and sum
      return periodArray.reduce((totalDays, monthStr) => {
        const [month, year] = monthStr.split('-');
        const fullYear = 2000 + parseInt(year);
        const monthIndex = monthMap[month];
        return totalDays + new Date(fullYear, monthIndex + 1, 0).getDate();
      }, 0);
      
    case 'quarterly':
      // For quarterly, calculate days in each quarter
      return periodArray.reduce((totalDays, quarterStr) => {
        // quarterStr format: "Q1'24", "Q2'24", etc.
        const quarterMatch = quarterStr.match(/Q(\d)'(\d+)/);
        if (!quarterMatch) return totalDays;
        
        const quarterNum = parseInt(quarterMatch[1]);
        const year = 2000 + parseInt(quarterMatch[2]);
        
        // Calculate days for each month in the quarter
        const quarterMonths = getMonthsInQuarter(quarterNum, year);
        return totalDays + quarterMonths.reduce((quarterDays, month) => {
          return quarterDays + new Date(year, month + 1, 0).getDate();
        }, 0);
      }, 0);
      
    case 'annual':
      // For annual, calculate days in each year
      return periodArray.reduce((totalDays, yearStr) => {
        const year = parseInt(yearStr);
        // Check if leap year
        const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        return totalDays + (isLeapYear ? 366 : 365);
      }, 0);
      
    default:
      return 0;
  }
};

/**
 * Helper function to get month indices for a given quarter
 * @param quarterNum - Quarter number (1-4)
 * @param year - Full year (e.g., 2024)
 * @returns Array of month indices (0-11)
 */
const getMonthsInQuarter = (quarterNum: number, year: number): number[] => {
  switch (quarterNum) {
    case 1: return [0, 1, 2]; // Jan, Feb, Mar
    case 2: return [3, 4, 5]; // Apr, May, Jun
    case 3: return [6, 7, 8]; // Jul, Aug, Sep
    case 4: return [9, 10, 11]; // Oct, Nov, Dec
    default: return [];
  }
};

/**
 * Get days for a single period string based on period type
 * @param periodStr - Period string (month, quarter, or year)
 * @param periodType - Period type: 'monthly', 'quarterly', or 'annual'
 * @returns Number of days in the period
 */
export const getPeriodDays = (
  periodStr: string,
  periodType: 'monthly' | 'quarterly' | 'annual'
): number => {
  return calculatePeriodDays(periodStr, periodType);
};

// Function to calculate TTM year-over-year growth
export const calculateTTMYoYGrowth = (
  accounts: AccountEntry[],
  categories: string[],
  allMonths: string[],
  isRevenue: boolean,
  isContraRevenue: boolean
): string => {
  // Sort all available months
  const sortedMonths = [...allMonths].sort(compareMonthYear);
  
  // Need at least 24 months of data for a YoY TTM comparison
  if (sortedMonths.length < 24) {
    return "n/a";
  }
  
  // Get the most recent 12 months
  const currentTTMMonths = sortedMonths.slice(-12);
  
  // Get the previous 12 months before that
  const previousTTMMonths = sortedMonths.slice(-24, -12);
  
  // Calculate current TTM total
  const currentTTM = categories.reduce((total, category) => 
    total + calculateTTM(accounts, category, currentTTMMonths, isRevenue, isContraRevenue), 0);
  
  // Calculate previous TTM total
  const previousTTM = categories.reduce((total, category) => 
    total + calculateTTM(accounts, category, previousTTMMonths, isRevenue, isContraRevenue), 0);
  
  // Return the growth percentage
  return calculateGrowthPercentage(currentTTM, previousTTM);
};

// Add this new function to your calculations.ts file

/**
 * Calculates year-over-year growth for a year-to-date period
 * Handles special case for current year by comparing to same months in previous year
 */
 // Updated calculateYTDGrowthPercentage function
 export const calculateYTDGrowthPercentage = (
  accounts: AccountEntry[],
  revenueCategories: string[],
  contraRevenueCategories: string[],
  allMonths: string[],
  currentYear: string,
  previousYear: string
): string => {
  // Null safety check
  if (!currentYear || !previousYear || !allMonths) {
    return "n/a";
  }
  
  // Convert years to the format used in month strings (2-digit year)
  const currentYearShort = currentYear.length === 4 ? currentYear.slice(2) : currentYear;
  const previousYearShort = previousYear.length === 4 ? previousYear.slice(2) : previousYear;
  
  // Sort all months chronologically
  const sortedMonths = [...allMonths].sort(compareMonthYear);
  
  // Identify current year YTD months
  const currentYearMonths = sortedMonths.filter(month => {
    const yearPart = month.split('-')[1];
    return yearPart === currentYearShort;
  });
  
  if (currentYearMonths.length === 0) {
    return "n/a";
  }
  
  // Identify the same months in previous year
  const monthNames = currentYearMonths.map(month => month.split('-')[0]);
  const previousYearMonths = monthNames.map(month => `${month}-${previousYearShort}`);
  
  // Check if we have data for the previous year months
  const availablePrevMonths = previousYearMonths.filter(month => allMonths.includes(month));
  if (availablePrevMonths.length === 0) {
    return "n/a";
  }
  
  // Calculate current year YTD net revenue
  const currentYearRevenue = [
    ...revenueCategories.map(category => ({ category, isRevenue: true, isContra: false })),
    ...contraRevenueCategories.map(category => ({ category, isRevenue: false, isContra: true }))
  ].reduce((total, { category, isRevenue, isContra }) => {
    return total + currentYearMonths.reduce((monthTotal, month) => {
      return monthTotal + calculateCategoryTotal(
        accounts, category, month, 'monthly', isRevenue, isContra
      );
    }, 0);
  }, 0);
  
  // Calculate previous year YTD net revenue
  const previousYearRevenue = [
    ...revenueCategories.map(category => ({ category, isRevenue: true, isContra: false })),
    ...contraRevenueCategories.map(category => ({ category, isRevenue: false, isContra: true }))
  ].reduce((total, { category, isRevenue, isContra }) => {
    return total + availablePrevMonths.reduce((monthTotal, month) => {
      return monthTotal + calculateCategoryTotal(
        accounts, category, month, 'monthly', isRevenue, isContra
      );
    }, 0);
  }, 0);
  
  console.log('YTD Comparison Debug:', {
    currentYear,
    currentYearMonths: currentYearMonths.join(', '),
    previousYear,
    availablePrevMonths: availablePrevMonths.join(', '),
    currentYearRevenue,
    previousYearRevenue
  });
  
  return calculateGrowthPercentage(currentYearRevenue, previousYearRevenue);
};

// RatioLineItem type for fin_ratios
export interface RatioLineItem {
  name: string;
  type: 'ratio';
  level: number;
  formula: string;
  description: string;
  account_mappings: string[];
  values: number[];
}

import { FinancialReportData } from '../data/financialReportsHelper';

export function calculateFinancialRatiosForSync(
  reports: Record<string, FinancialReportData | null>,
  periods: string[]
): { periods: string[], line_items: RatioLineItem[] } {
  // Example ratios (expand as needed)
  const ratioDefs = [
    {
      name: 'Current Ratio',
      formula: 'Total Current Assets ÷ Total Current Liabilities',
      description: 'Measures the company\'s ability to pay short-term obligations.',
      account_mappings: ['Total Current Assets', 'Total Current Liabilities'],
      calc: (metrics: any) => metrics.currentLiabilities === 0 ? 0 : metrics.currentAssets / metrics.currentLiabilities
    },
    {
      name: 'Quick Ratio',
      formula: '(Cash + Short-Term Investments + Trade Accounts Receivable) ÷ Total Current Liabilities',
      description: 'Measures liquidity excluding inventory.',
      account_mappings: ['Cash', 'Short-Term Investments', 'Trade Accounts Receivable', 'Total Current Liabilities'],
      calc: (metrics: any) => metrics.currentLiabilities === 0 ? 0 : (metrics.cash + metrics.shortTermInvestments + metrics.accountsReceivable) / metrics.currentLiabilities
    },
    {
      name: 'Return on Assets',
      formula: 'Net Income ÷ Total Assets',
      description: 'Measures profitability relative to total assets.',
      account_mappings: ['Net Income', 'Total Assets'],
      calc: (metrics: any) => metrics.totalAssets === 0 ? 0 : metrics.netIncome / metrics.totalAssets
    },
    // Add more ratios as needed
  ];

  // Helper to get metrics for a period
  function getMetrics(period: string) {
    const getVal = (report: FinancialReportData | null, item: string) => {
      const value = report?.data?.[item]?.[period];
      if (value === undefined) {
        console.warn(`[Ratios] Missing value for line item '${item}' in period '${period}'`);
        return null;
      }
      return value;
    };
    return {
      currentAssets: getVal(reports.bs_detail, 'Total Current Assets'),
      currentLiabilities: getVal(reports.bs_detail, 'Total Current Liabilities'),
      cash: getVal(reports.bs_detail, 'Cash'),
      shortTermInvestments: getVal(reports.bs_detail, 'Short-Term Investments'),
      accountsReceivable: getVal(reports.bs_detail, 'Trade Accounts Receivable'),
      totalAssets: getVal(reports.bs_detail, 'Total Assets'),
      netIncome: getVal(reports.pnl_detail, 'Net Income'),
      // Add more as needed
    };
  }

  const line_items: RatioLineItem[] = ratioDefs.map(ratio => {
    const values = periods.map(period => {
      const metrics = getMetrics(period);
      // If any required metric is null, return null for this ratio value
      if (Object.values(metrics).some(v => v === null)) return null;
      return ratio.calc(metrics);
    });
    return {
      name: ratio.name,
      type: 'ratio',
      level: 1,
      formula: ratio.formula,
      description: ratio.description,
      account_mappings: ratio.account_mappings,
      values
    };
  });

  return { periods, line_items };
}
